import react, { useState, useCallback, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import Fuse from 'fuse.js';
import moment from "moment";

import { Button, AutoCompleteField } from "../../../helpers"
import { getFullName, sortArrayOfObjectsInAlphabeticalOrder } from "../../../utils"

import { sendWhatsAppMessage } from "../../../services/api/whatsappMessage"
import { raiseToast } from "../../../services/redux/reducers/toast/toast"
import { getPMCompanyConfig } from "../../../setup/pmCompanyConfig";
import { fetchApprovalSecondary } from "../../../services/redux/reducers/commonThunk";
import { DATE_FORMAT } from "../../../constants";

const QuickApprovalReq = (props) => {
    const { selectedApproval, setIsPageLoading, hideQuickApprovalReqPanel } = props

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(fetchApprovalSecondary())
    }, [])

    const [userSuggestions, setUserSuggestions] = useState([])
    const [selectedUser, setSelectedUser] = useState(null)

    const userDetails = useSelector((state) => state.userDetails)
    const approvalSecondary = useSelector((state) => state.common.approvalSecondary)
    let pmCompanyUsers = userDetails.PMCompanyDetails.companyUsers

    const pmCompanyConfig = getPMCompanyConfig(userDetails.PMCompanyDetails.companyId)

    const todayDate = moment(new Date()).format(DATE_FORMAT)

    const relatedApprovalSecondary = approvalSecondary.filter(eachApprovalSecondary => {
        const startDate = moment(eachApprovalSecondary.startDate).format(DATE_FORMAT)
        const endDate = moment(eachApprovalSecondary.endDate).format(DATE_FORMAT)

        return eachApprovalSecondary.approvalId === selectedApproval.approvalId && todayDate >= startDate && todayDate <= endDate
    })

    const relatedSecondaryApproverIds = relatedApprovalSecondary.map(eachApprovalSecondary => eachApprovalSecondary.secondaryUserId)

    let quickApprovalReqUserIds = [5, 41, 174, 22632, 52, 308] // users to send quick approval request through whatsapp in case urgency
    if (relatedSecondaryApproverIds.length) {
        quickApprovalReqUserIds = [...quickApprovalReqUserIds, ...relatedSecondaryApproverIds]
    }

    quickApprovalReqUserIds = new Set(quickApprovalReqUserIds)
    let quickApprovalReqUsers = pmCompanyUsers.reduce((acc, eachUser) => {
        if (quickApprovalReqUserIds.has(eachUser.userId)) {
            acc.push({
                ...eachUser,
                fullName: getFullName(eachUser.firstName, eachUser.lastName)
            })
        }
        return acc
    }, [])

    quickApprovalReqUsers = sortArrayOfObjectsInAlphabeticalOrder(quickApprovalReqUsers, "firstName")

    const userFuzzSearch = useCallback(new Fuse(quickApprovalReqUsers, {
        keys: ['fullName'],
        includeScore: true,
        threshold: 0.5,
        ignoreLocation: true,
        shouldSort: true,
        getFn: (obj) => {
            return obj.fullName.replace(/\s+/g, '')
        },
    }), [quickApprovalReqUsers])

    const userSearchMethod = (event) => {
        if (event.query === "") {
            setUserSuggestions([...quickApprovalReqUsers])
        }
        else {
            const searchResults = userFuzzSearch.search(event.query)
            const relatedUsers = searchResults.map(eachProduct => eachProduct.item)
            setUserSuggestions(relatedUsers)
        }
    }

    const handleUserChange = (e) => {
        setSelectedUser(e.target.value)
    }
    const userItemTemplate = (item) => {
        const isSecondaryApprover = relatedApprovalSecondary.some(eachApprovalSecondary => eachApprovalSecondary.secondaryUserId === item.userId)
        return (
            <div className="flex align-items-center">
                <span>{item.fullName}</span>
                {isSecondaryApprover && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
            </div>
        )
    }
    
    const handleSendWhatsAppMessage = async () => {
        if(!selectedUser){
            return dispatch(raiseToast({ severity: 'error', detail: "Please select user.", life: 3000 }))
        }

        const remarks = selectedApproval.remarks || ""

        let whatsappMessages = []
        whatsappMessages.push({
            "campaignName": "approval_template_procmart",
            "destination": selectedUser.phoneNumber,
            "userName": "ProcMart",
            "templateParams": [
                `${selectedUser.firstName}`,
                `${selectedApproval.approvalTitle}`,
                `${getFullName(userDetails.userNameDetails.firstName, userDetails.userNameDetails.lastName)}`,
                `${remarks.replace(/\s+/g, ' ').trim()}`,
                `${selectedApproval.approvalInstanceId}`
            ],
            "source": "new-landing-page form",
            "paramsFallbackValue": {
                "FirstName": "user"
            }
        })

        if (window.confirm('This will send a WhatsApp message. Are you sure you want to continue?')) {
            const data = {
                messages: whatsappMessages
            }

            setIsPageLoading(true)
            const response = await sendWhatsAppMessage(data)
            setIsPageLoading(false)

            if (!response.failure) {
                hideQuickApprovalReqPanel()
                return dispatch(raiseToast({ severity: 'success', detail: "Whatsapp message has sent successfully" }))
            } else {
                return dispatch(raiseToast({ severity: 'error', detail: response.data.error }))
            }
        }
    }

    return (
        <div className="p-3 mb-0">
            <div className="grid">
                <div className="flex flex-column col-12 mb-2 lg:col-12 lg:mb-0">
                    <AutoCompleteField labelText="Select User" field="fullName" value={selectedUser} searchMethod={userSearchMethod} suggestions={userSuggestions} onChange={handleUserChange} itemTemplate={userItemTemplate} dropdown required />
                </div>
            </div>
            <div className="flex justify-content-end mt-2">
                <Button label="Submit" onClick={handleSendWhatsAppMessage} />
            </div>
        </div>
    )
}

export default QuickApprovalReq